export const viewTransitionRouterScript = `
(() => {
  if (window.__playbrainViewTransitionRouter) return;
  window.__playbrainViewTransitionRouter = true;

  const localePattern = "(?:en|ja)";
  const newsDetailPattern = new RegExp("^/" + localePattern + "/news/([^/?#]+)/*$");
  const newsIndexPattern = new RegExp("^/" + localePattern + "/news/*$");
  const careersDetailPattern = new RegExp("^/" + localePattern + "/careers/([^/?#]+)/*$");
  const careersIndexPattern = new RegExp("^/" + localePattern + "/careers/*$");
  const newsSlug = (pathname) => pathname && pathname.match(newsDetailPattern)?.[1];
  const careersSlug = (pathname) => pathname && pathname.match(careersDetailPattern)?.[1];
  const isNewsIndex = (pathname) => !!pathname && newsIndexPattern.test(pathname);
  const isCareersIndex = (pathname) => !!pathname && careersIndexPattern.test(pathname);
  const listScrollStoragePrefix = "playbrain:list-scroll:";
  const normalizedListPath = (pathname) => pathname?.replace(/\\/+$/, "") || "";
  const transitionTypes = (fromPathname, toPathname) => {
    const toSlug = newsSlug(toPathname);
    if (toSlug) return ["news-" + toSlug];
    const fromSlug = newsSlug(fromPathname);
    if (fromSlug && isNewsIndex(toPathname)) return ["news-" + fromSlug, "back"];
    const toCareersSlug = careersSlug(toPathname);
    if (toCareersSlug) return ["careers-" + toCareersSlug];
    const fromCareersSlug = careersSlug(fromPathname);
    if (fromCareersSlug && isCareersIndex(toPathname)) return ["careers-" + fromCareersSlug, "back"];
    return [];
  };
  const listScrollPathForDetailNavigation = (fromPathname, toPathname) => {
    if (isNewsIndex(fromPathname) && newsSlug(toPathname)) return normalizedListPath(fromPathname);
    if (isCareersIndex(fromPathname) && careersSlug(toPathname)) return normalizedListPath(fromPathname);
    return "";
  };
  const listScrollPathForBackNavigation = (fromPathname, toPathname) => {
    if (newsSlug(fromPathname) && isNewsIndex(toPathname)) return normalizedListPath(toPathname);
    if (careersSlug(fromPathname) && isCareersIndex(toPathname)) return normalizedListPath(toPathname);
    return "";
  };
  const listScrollStorageKey = (listPath) => listPath ? listScrollStoragePrefix + listPath : "";
  const saveListScrollPosition = (fromPathname, toPathname) => {
    const listPath = listScrollPathForDetailNavigation(fromPathname, toPathname);
    const storageKey = listScrollStorageKey(listPath);
    if (!storageKey) return;

    try {
      sessionStorage.setItem(storageKey, String(window.scrollY));
    } catch {}
  };
  const savedListScrollPosition = (fromPathname, toPathname) => {
    const listPath = listScrollPathForBackNavigation(fromPathname, toPathname);
    const storageKey = listScrollStorageKey(listPath);
    if (!storageKey) return null;

    try {
      const value = Number(sessionStorage.getItem(storageKey));
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  };
  const restoreSavedListScrollPosition = (scrollY) => {
    if (!Number.isFinite(scrollY)) return;

    window.scrollTo(0, scrollY);
  };
  const scheduleSavedListScrollRestores = (scrollY) => {
    if (!Number.isFinite(scrollY)) return;

    restoreSavedListScrollPosition(scrollY);
    requestAnimationFrame(() => {
      restoreSavedListScrollPosition(scrollY);
      requestAnimationFrame(() => {
        restoreSavedListScrollPosition(scrollY);
      });
    });
    [80, 200, 500].forEach((delay) => {
      window.setTimeout(() => restoreSavedListScrollPosition(scrollY), delay);
    });
  };
  const restoreAfterTransitionReady = (transition, scrollY) => {
    if (!Number.isFinite(scrollY)) return;

    const restore = () => scheduleSavedListScrollRestores(scrollY);

    transition.ready.then(restore).catch(restore);
  };
  const clearActiveContentTransitions = () => {
    document.querySelectorAll("[data-content-transition-element][data-content-transition-name]").forEach((element) => {
      element.style.viewTransitionName = "";
    });
  };
  const activateContentTransition = (transitionName) => {
    if (!transitionName) return;
    clearActiveContentTransitions();
    const selector = "[data-content-transition-element][data-content-transition-name='" + CSS.escape(transitionName) + "']";
    document.querySelectorAll(selector).forEach((element) => {
      element.style.viewTransitionName = transitionName;
    });
  };
  const forceInstantScroll = () => {
    const previous = {
      html: document.documentElement.style.scrollBehavior,
      body: document.body.style.scrollBehavior,
    };
    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.scrollBehavior = "auto";
    return () => {
      document.documentElement.style.scrollBehavior = previous.html;
      document.body.style.scrollBehavior = previous.body;
    };
  };
  const shouldUseDocumentNavigation = (event, anchor) =>
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey ||
    anchor.target ||
    anchor.hasAttribute("download");
  const waitForRouter = () => {
    const router = window.__TSR_ROUTER__;
    if (router?.navigate) return Promise.resolve(router);

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const check = () => {
        const nextRouter = window.__TSR_ROUTER__;
        if (nextRouter?.navigate) {
          resolve(nextRouter);
          return;
        }
        if (performance.now() - startedAt > 6000) {
          resolve(null);
          return;
        }
        requestAnimationFrame(check);
      };

      check();
    });
  };
  const transitionLinkAnchor = (event) => event.target?.closest?.("a[data-view-transition-link='true']");
  const markPerformance = (name) => {
    try {
      performance.mark(name);
    } catch {}
  };
  const preloadedRoutes = new Set();
  const preloadAnchorRoute = async (anchor) => {
    if (!anchor) return;

    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    const to = url.pathname + url.search + url.hash;
    if (to === window.location.pathname + window.location.search + window.location.hash) return;
    if (preloadedRoutes.has(to)) return;
    preloadedRoutes.add(to);

    const router = await waitForRouter();
    if (!router?.preloadRoute) {
      preloadedRoutes.delete(to);
      return;
    }
    void router.preloadRoute({ to }).catch(() => {
      preloadedRoutes.delete(to);
    });
  };
  const preloadFromIntent = (event) => {
    const anchor = transitionLinkAnchor(event);
    if (!anchor) return;
    if (event.type === "pointerover" && anchor.contains(event.relatedTarget)) return;
    void preloadAnchorRoute(anchor);
  };

  ["pointerover", "focusin", "touchstart"].forEach((eventName) => {
    document.addEventListener(eventName, preloadFromIntent, { passive: true });
  });

  document.addEventListener(
    "click",
    async (event) => {
      const anchor = transitionLinkAnchor(event);
      if (!anchor || shouldUseDocumentNavigation(event, anchor)) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      const to = url.pathname + url.search + url.hash;
      const types = transitionTypes(window.location.pathname, url.pathname);
      const activeTransitionName = anchor.dataset.contentTransitionName || anchor.dataset.newsTransitionName || types.find((type) => type !== "back");
      const savedScrollY = savedListScrollPosition(window.location.pathname, url.pathname);
      saveListScrollPosition(window.location.pathname, url.pathname);
      event.preventDefault();
      markPerformance("playbrain:route-start");
      const router = await waitForRouter();
      if (!router?.navigate) {
        window.location.assign(url.href);
        return;
      }

      activateContentTransition(activeTransitionName);
      const restoreScrollBehavior = forceInstantScroll();
      const update = () =>
        Promise.resolve(router.navigate({ to })).then(() => {
          activateContentTransition(activeTransitionName);
          restoreSavedListScrollPosition(savedScrollY);
          markPerformance("playbrain:route-content-ready");
        });
      if (!document.startViewTransition || types.length === 0) {
        void update()
          .then(() => scheduleSavedListScrollRestores(savedScrollY))
          .finally(restoreScrollBehavior);
        return;
      }

      const transition = document.startViewTransition({ update, types });
      restoreAfterTransitionReady(transition, savedScrollY);
      transition.finished.finally(() => {
        activateContentTransition(activeTransitionName);
        scheduleSavedListScrollRestores(savedScrollY);
        restoreScrollBehavior();
      });
    },
    true
  );
})();
`
